import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import Input from '@material-ui/core/Input';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Divider, FormControl, IconButton, InputLabel, TextField } from '@mui';
import { makeStyles } from '@material-ui/core/styles';
import * as colors from '@constants/colors';
import { sortByField } from '@utils/data-types-utils';
import FilterChipMenuItem from '../filters/chips/shared/menu-item';
import FilterChipPopover from '../filters/chips/shared/popover';

const stopPropagation = event => event.stopPropagation();

const caseInsensitiveFilterOptions = (searchTerm, options) => {
  if (!searchTerm || typeof searchTerm !== 'string' || !searchTerm.trim()) {
    return options;
  }
  const regex = new RegExp(searchTerm, 'i');
  return options.filter(option => regex.test(option.name));
};

const useStyles = makeStyles(() => ({
  searchField: {
    margin: '8px 16px',
    width: 'calc(100% - 32px)'
  },
  searchInput: {
    fontSize: '14px'
  },
  menuList: {
    maxHeight: '200px',
    overflowY: 'auto'
  },
  selectRoot: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: colors.dotmapsBlack80,
    fontSize: '0.875rem',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  selectRootDisabled: {
    color: 'rgba(0, 0, 0, 0.38)',
    cursor: 'default'
  },
  selectMenu: {
    zIndex: 1300
  },
  selectContents: {
    minWidth: '200px',
    maxWidth: '400px'
  },
  inputRoot: {
    cursor: 'pointer',
    '&:before': {
      borderBottom: `1px solid ${colors.dotmapsBlack40}`
    },
    '&:hover:not(.Mui-disabled):before': {
      borderBottom: `2px solid ${colors.dotmapsBlack80}`
    },
    '&.Mui-focused:after': {
      borderBottom: `2px solid ${colors.dotmapsBlue}`
    },
    '&.Mui-disabled:before': {
      borderBottomStyle: 'solid',
      borderBottomColor: colors.dotmapsBlack20
    }
  },
  inputElement: {
    cursor: 'pointer',
    caretColor: 'transparent',
    '&.Mui-disabled': {
      color: colors.dotmapsBlack40,
      WebkitTextFillColor: colors.dotmapsBlack40,
      cursor: 'default'
    }
  },
  dropdownIcon: {
    color: 'rgba(0, 0, 0, 0.54)',
    marginRight: '-4px'
  },
  dropdownIconDisabled: {
    color: colors.dotmapsBlack40,
    marginRight: '-4px'
  }
}));

const SearchableAgencySelect = ({
  disabled,
  floatingLabelText,
  errorText,
  onChange,
  value,
  dataName,
  dataSortField,
  style
}) => {
  const classes = useStyles();
  const inputRef = useRef(null);

  const dataTypes = useSelector(state => state.dataTypes);
  const agencies = useMemo(() => dataTypes[dataName] || {}, [dataTypes, dataName]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');

  const filteredOptions = useMemo(() => {
    const options = Object.values(agencies);

    // Filter out inactive agencies and 'No Designation'
    const activeOptions = R.pickBy(option => (
      (!Object.prototype.hasOwnProperty.call(option, 'active') || option.active) &&
      option.name !== 'No Designation'
    ), options);

    const sortedOptions = sortByField(dataSortField || 'name')(R.values(activeOptions));

    if (!searchFilter || typeof searchFilter !== 'string' || !searchFilter.trim()) {
      return sortedOptions;
    }

    return caseInsensitiveFilterOptions(searchFilter, sortedOptions);
  }, [agencies, dataSortField, searchFilter]);

  const handleOpen = useCallback((event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  }, [disabled]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchFilter('');
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleOpen(event);
    } else if (event.key === 'Escape' && anchorEl) {
      event.preventDefault();
      event.stopPropagation();
      handleClose();
    } else {
      stopPropagation(event);
    }
  }, [handleOpen, handleClose, anchorEl]);

  const handleSelectItem = useCallback((agencyValue) => {
    onChange(agencyValue);
    handleClose();
  }, [onChange, handleClose]);

  const handleSearchChange = useCallback((event) => {
    setSearchFilter(event.target.value || '');
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchFilter('');
  }, []);

  const displayValue = useMemo(() => {
    if (!value) {
      return '';
    }
    const selectedOption = Object.values(agencies).find(option => option.id == value);  // eslint-disable-line eqeqeq
    return selectedOption ? selectedOption.name : '';
  }, [value, agencies]);

  // Determine if label should shrink (when there's a value or dropdown is open)
  const shouldShrink = !!displayValue || !!anchorEl;

  return (
    <FormControl style={style} error={!!errorText} fullWidth focused={false}>
      {floatingLabelText && (
        <InputLabel
          shrink={shouldShrink}
          style={disabled ? { color: colors.dotmapsBlack40 } : {}}
        >
          {floatingLabelText}
        </InputLabel>
      )}
      <Input
        ref={inputRef}
        readOnly
        disabled={disabled}
        value={displayValue}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        classes={{
          root: classes.inputRoot,
          input: classes.inputElement
        }}
        inputProps={{
          'aria-haspopup': 'listbox',
          'aria-expanded': !!anchorEl
        }}
        endAdornment={
          <InputAdornment position="end">
            <ArrowDropDownIcon className={disabled ? classes.dropdownIconDisabled : classes.dropdownIcon} />
          </InputAdornment>
        }
      />
      <FilterChipPopover
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        classes={{
          root: classes.selectMenu,
          paper: classes.selectContents
        }}
      >
        <TextField
          variant="standard"
          fullWidth
          value={searchFilter}
          placeholder="Search agencies..."
          onChange={handleSearchChange}
          onKeyDown={stopPropagation}
          className={classes.searchField}
          inputRef={input => input && input.focus()}
          InputProps={{
            classes: {
              input: classes.searchInput
            },
            endAdornment: (
              <InputAdornment position="end">
                {searchFilter.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={handleSearchClear}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Divider />
        <MenuList className={classes.menuList}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map(agency => (
              <FilterChipMenuItem
                key={agency.id}
                option={{ label: agency.name, value: agency.id }}
                onSelect={handleSelectItem}
                checked={value == agency.id}  // eslint-disable-line eqeqeq
              />
            ))
          ) : (
            <MenuItem disabled>No agencies found</MenuItem>
          )}
        </MenuList>
      </FilterChipPopover>
      {errorText && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};

SearchableAgencySelect.propTypes = {
  dataName: PropTypes.string,
  dataSortField: PropTypes.string,
  disabled: PropTypes.bool,
  errorText: PropTypes.string,
  floatingLabelText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  style: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

SearchableAgencySelect.defaultProps = {
  dataName: 'agency',
  dataSortField: 'name'
};

export default memo(SearchableAgencySelect);
